import type { SessionEndReason, TerminalCommand, TerminalTranscript, ViewerMessage } from '@api/remote/contracts';
import { approveRemoteSession, endRemoteSession, isStatus, listApprovalKeys, openRemoteSession, terminalSocketUrl } from '../lib/api';
import { describeKeyError, signWithKey, webauthnAvailable } from '../lib/webauthn';
import { type ScreenPart, screenText } from './transcript-screen';

/**
 * A full-access PowerShell terminal through WITTCO Remote (WRMM-68): asked for, approved once with the technician's key,
 * then a live connection that lasts until it is closed. What is typed goes as UTF-8 binary frames; what the device shows
 * comes back as binary frames for the terminal emulator; the relay's own messages are JSON text.
 */
export interface TerminalState {
  phase: 'starting' | 'approving' | 'connecting' | 'connected' | 'ended';
  sessionId: string | null;
  approval: { busy: boolean; error: string | null } | null;
  notice: string | null;
  ended: { reason: SessionEndReason | 'refused'; message: string } | null;
  connectedAt: number | null;
}
export const initialTerminal: TerminalState = { phase: 'starting', sessionId: null, approval: null, notice: null, ended: null, connectedAt: null };

export class TerminalSession {
  state: TerminalState = initialTerminal;
  private socket: WebSocket | null = null;
  private closed = false;
  private approvalOptions: Record<string, unknown> | null = null;
  private readonly encoder = new TextEncoder();

  constructor(private readonly options: {
    mspId: string; deviceId: string; terminalMode?: 'shell' | 'support' | 'tmux' | 'tmux-view'; size: () => { cols: number; rows: number };
    onChange: (state: TerminalState) => void; onOutput: (data: Uint8Array) => void;
  }) {}

  private set(patch: Partial<TerminalState>) { this.state = { ...this.state, ...patch }; this.options.onChange(this.state); }

  async start() {
    let ticket;
    try { ticket = await openRemoteSession(this.options.mspId, this.options.deviceId, 'control', undefined, 'terminal', this.options.terminalMode ?? 'shell'); } catch (error) {
      const status = (error as { status?: number }).status;
      const keyless = status === 409 && await listApprovalKeys().then(keys => !keys.length, () => false);
      this.set({ phase: 'ended', ended: { reason: 'refused', message: keyless
        ? 'A full-access terminal needs your security key or passkey, and you haven’t registered one. Add one in Settings.'
        : status === 403 ? 'A full-access terminal takes remote access and running and approving scripts on this device.'
        : status === 409 ? 'The device isn’t connected to WITTCO Remote right now. Check that it’s on and online, then try again.'
        : status === 404 ? 'This device isn’t available to you.' : 'The terminal couldn’t be started. Try again in a moment.' } });
      return;
    }
    if (this.closed) { this.abandon(ticket.id); return; }
    this.approvalOptions = ticket.approval;
    this.set({ phase: 'approving', sessionId: ticket.id, approval: { busy: false, error: webauthnAvailable() ? null
      : 'Security keys need a secure address: open the console at localhost or over HTTPS.' } });
  }

  /** The technician's key approves this one session; then it connects. Called from a click (browsers require one). */
  async approve() {
    const sessionId = this.state.sessionId, options = this.approvalOptions;
    if (this.state.phase !== 'approving' || !sessionId || !options || this.state.approval?.busy) return;
    this.set({ approval: { busy: true, error: null } });
    try {
      await approveRemoteSession(this.options.mspId, sessionId, await signWithKey(options));
    } catch (error) {
      if (this.closed) return;
      if (isStatus(error, 404) || isStatus(error, 403)) {
        this.abandon(sessionId);
        this.set({ phase: 'ended', ended: { reason: 'refused', message: 'The approval wasn’t accepted or took too long. Open the terminal again to retry.' } });
        return;
      }
      this.set({ approval: { busy: false, error: (error as { status?: number })?.status ? 'The server didn’t accept the approval.' : describeKeyError(error) } });
      return;
    }
    if (!this.closed) this.connect(sessionId);
  }

  private connect(sessionId: string) {
    this.set({ phase: 'connecting', sessionId, approval: null });
    const { cols, rows } = this.options.size();
    const socket = new WebSocket(terminalSocketUrl(this.options.mspId, sessionId, cols, rows));
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    socket.onmessage = event => {
      if (typeof event.data !== 'string') { this.options.onOutput(new Uint8Array(event.data as ArrayBuffer)); return; }
      let message: ViewerMessage;
      try { message = JSON.parse(event.data) as ViewerMessage; } catch { return; }
      if (message.type === 'state') this.set(message.state === 'connected' ? { phase: 'connected', connectedAt: Date.now() } : { phase: 'connecting' });
      else if (message.type === 'notice') this.set({ notice: message.message });
      else if (message.type === 'ended') this.set({ phase: 'ended', ended: { reason: message.reason, message: message.message } });
    };
    socket.onclose = () => {
      if (this.state.phase === 'ended') return;
      this.abandon(sessionId);
      this.set({ phase: 'ended', ended: { reason: 'failed', message: 'The connection to the terminal was lost.' } });
    };
  }

  /** What the technician typed or pasted. */
  input(text: string) {
    if (this.state.phase !== 'connected' || this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(this.encoder.encode(text));
  }

  resize(cols: number, rows: number) {
    if (this.state.phase !== 'connected' || this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify({ type: 'resize', cols, rows } satisfies TerminalCommand));
  }

  /** Ends the session; the relay records it and closes PowerShell on the device. */
  end() {
    this.closed = true;
    const sessionId = this.state.sessionId;
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify({ type: 'end' } satisfies TerminalCommand));
    this.socket?.close();
    if (sessionId && this.state.phase !== 'ended') this.abandon(sessionId);
    if (this.state.phase !== 'ended') this.set({ phase: 'ended', ended: { reason: 'closed', message: 'The terminal was closed.' } });
  }

  private abandon(sessionId: string) { void endRemoteSession(this.options.mspId, sessionId).catch(() => undefined); }
}

/** Keys as typed, readable: Enter ends a line, other control keys show as ^X, escape sequences (arrows and the like) as ⟨ESC…⟩. */
const keys = (typed: string) => typed
  .replace(/\u001b(\[[0-?]*[ -/]*[@-~]|O.|.)?/g, sequence => `⟨ESC${sequence.slice(1)}⟩`)
  .replace(/\r\n?/g, '⏎\n')
  .replace(/[\u0000-\u0009\u000b-\u001f\u007f]/g, c => (c === '\u007f' ? '⌫' : `^${String.fromCharCode(c.charCodeAt(0) + 64)}`));

/** What the typed-keys part says when the server didn't include it (WRMM-172). */
const TYPED_WITHHELD: Record<Exclude<TerminalTranscript['typed'], 'included'>, string> = {
  owners_only: 'Visible to MSP-wide owners only.',
  not_kept: 'Not kept. This session is from before typed keys were encrypted, and they were deleted then.',
  unavailable: 'Couldn’t be decrypted right now. Try again in a moment.',
};

/**
 * A terminal session's transcript as plain text, after a heading for the session: what the device showed, as the
 * terminal displayed it (replayed through a terminal emulator: screenText, WRMM-171); then everything typed, key by
 * key, including what the device didn't show (a password at a prompt, say), as the server recorded it, for MSP-wide
 * owners only (WRMM-172).
 */
export async function transcriptText(transcript: TerminalTranscript, heading: string): Promise<string> {
  const rule = (title: string) => `${title}\n${'-'.repeat(Math.min(title.length, 80))}`;
  if (transcript.expired && !transcript.entries.length) {
    return `${rule(heading)}\nDeleted: transcripts are kept for 30 days.\n`;
  }
  // What the device showed, with the terminal's size whenever the relay recorded one (WRMM-171).
  const shown = transcript.entries.flatMap((e): ScreenPart[] => {
    if (e.direction === 'out') return [{ text: e.text }];
    const [cols, rows] = e.direction === 'size' ? e.text.split(';').map(Number) : [];
    return cols && rows ? [{ cols, rows }] : [];
  });
  const text = await screenText(shown);
  const typed = transcript.typed === 'included'
    ? keys(transcript.entries.filter(e => e.direction === 'in').map(e => e.text).join('')).trim() : TYPED_WITHHELD[transcript.typed];
  return `${rule(heading)}\n${text.trim()}\n\n${rule('Everything typed, as the keys were sent')}\n${typed}\n`;
}
